import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { FastTrackRouteQueryParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    GetPaymentDetailService,
    InvoiceDetailsItemResponse,
    InvoiceDetailsRequest,
    ListInvoiceDetailsService,
    PaymentDetailDto,
    PaymentDetailRequest
} from '@lsq-frontend/remote-services/financing-graphql';
import { Color, LifecycleAware } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';

@LifecycleAware()
@Component({
    selector: 'invoice-detail-page',
    template: `
        <div *ngIf="isLoading">
            <lsq-circle-loader></lsq-circle-loader>
        </div>
        <div *ngIf="detailsDataSource">
            <page-header
                [titleText]="pageTitle"
                [backButtonText]="backText"
                (handleBackButtonClick)="navigateBack()"
                [style.marginBottom]="'0rem'"
            ></page-header>
        </div>
        <div
            *ngIf="detailsDataSource"
            class="scf-invoice-detail-page container-fluid page-container"
        >
            <div class="card-container">
                <invoice-detail-card
                    [invoiceDetailsDataSource]="detailsDataSource"
                ></invoice-detail-card>
            </div>
        </div>
        <div
            *ngIf="paymentDetailsDataSource && !(paymentDetailsDataSource.noData$ | async)"
            class="scf-invoice-detail-page container-fluid page-container"
        >
            <div class="card-container">
                <invoice-payment-detail-card
                    [paymentDetailsDataSource]="paymentDetailsDataSource"
                ></invoice-payment-detail-card>
            </div>
        </div>
    `,
    styleUrls: ['./invoice-detail-page.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerAccountInvoiceDetailPageComponent implements OnInit, OnDestroy {
    public invoiceTitlePrefix = this._i18n('Invoice #');
    public readonly processingColor = Color.Yellow;

    public readonly pendingCreditMemosBackText = this._i18n('Pending Credit Memos');
    public readonly paymentBackText = this._i18n('Payment');
    public readonly paymentDateI18n = this._i18n('Payment Date');

    public readonly backText = this._i18n('Back');

    public icons = Icons;
    public color = Color;
    public pageTitle: string;

    public detailsDataSource: LsqListSource<InvoiceDetailsItemResponse>;
    public paymentDetailsDataSource: LsqListSource<PaymentDetailDto>;
    public invoiceDetailsItemResponse: InvoiceDetailsItemResponse;
    public isLoading = true;

    constructor(
        private _listInvoiceDetailsService: ListInvoiceDetailsService,
        private _listInvoicePaymentDetailsService: GetPaymentDetailService,
        private _activatedRoute: ActivatedRoute,
        private _i18n: I18n,
        private location: Location
    ) {}

    public async ngOnInit(): Promise<void> {
        await this.loadData();
    }

    private async loadData(): Promise<void> {
        this.isLoading = true;
        const params = await this._activatedRoute.params.pipe(take(1)).toPromise();
        const invoiceKey = params.invoiceKey;
        const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
        const isLSQPayer = queryParamMap.get(FastTrackRouteQueryParams.IsPayerLSQ) ?? 'UNKOWN';
        const response = await this._listInvoiceDetailsService.executeOnce(
            new InvoiceDetailsRequest({
                buyerKeysFilter: [],
                invoiceKey: invoiceKey,
                isCreditMemo:
                    queryParamMap.get(FastTrackRouteQueryParams.GroupedInvoiceType) === 'creditMemo'
            })
        );

        const responsePaymentDetail = await this._listInvoicePaymentDetailsService.executeOnce(
            new PaymentDetailRequest({
                invoiceKey: invoiceKey,
                isPayerLSQ: isLSQPayer === 'true'
            })
        );

        const rows = response.rows!! || [];
        if (rows.length) {
            this.pageTitle = this.invoiceTitlePrefix + rows[0].invoiceNumber!!;
        }

        const paymentRows = responsePaymentDetail.rows!! || [];
        this.paymentDetailsDataSource = new LsqListSource({
            rows$: of(paymentRows)
        });

        this.detailsDataSource = new LsqListSource({
            rows$: of(rows)
        });

        this.isLoading = false;
    }

    public ngOnDestroy(): void {}

    public navigateBack(): void {
        this.location.back();
    }
}

export enum InvoiceFundingStatusModifications {
    ADJUSTMENT = 'ADJUSTMENT',
    CREDIT_MEMO = 'CREDIT_MEMO'
}
